import { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { Discrepancy } from '../../types/schema';
import { useUserContext } from '../../contexts/UserContext';
import { resolveAndArchiveDiscrepancy } from '../../utils/issueResolveArchive';
import { ITEM_STATUS_CONTENT } from '../../utils/constants';
import { useAppContext } from '../../contexts/AppContext';
import { Btn } from '../ui';

interface IssueResolveArchiveModalProps {
    discrepancy: Discrepancy | null;
    onClose: () => void;
    onArchived?: () => void | Promise<void>;
}

const getFocusableElements = (container: HTMLElement): HTMLElement[] =>
    Array.from(
        container.querySelectorAll<HTMLElement>(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
    );

export const IssueResolveArchiveModal = ({ discrepancy, onClose, onArchived }: IssueResolveArchiveModalProps) => {
    const { companyUser, companyOwner } = useUserContext();
    const { setIssueCount } = useAppContext();
    const [resolutionNotes, setResolutionNotes] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);
    const dialogRef = useRef<HTMLDivElement>(null);
    const notesRef = useRef<HTMLTextAreaElement>(null);
    const previouslyFocusedRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        if (!discrepancy) {
            setResolutionNotes('');
            setValidationError(null);
        }
    }, [discrepancy]);

    useEffect(() => {
        if (!discrepancy) {
            return;
        }

        previouslyFocusedRef.current =
            document.activeElement instanceof HTMLElement ? document.activeElement : null;

        const focusTimer = window.setTimeout(() => {
            notesRef.current?.focus();
        }, 0);

        return () => {
            window.clearTimeout(focusTimer);
            previouslyFocusedRef.current?.focus();
        };
    }, [discrepancy?.id]);

    useEffect(() => {
        if (!discrepancy) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && !isSubmitting) {
                event.preventDefault();
                onClose();
                return;
            }

            if (event.key !== 'Tab' || !dialogRef.current) {
                return;
            }

            const focusables = getFocusableElements(dialogRef.current);
            if (focusables.length === 0) {
                return;
            }

            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            const active = document.activeElement;

            if (event.shiftKey && active === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && active === last) {
                event.preventDefault();
                first.focus();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [discrepancy, isSubmitting, onClose]);

    if (!discrepancy) {
        return null;
    }

    const modalTitleId = `issue-resolve-archive-title-${discrepancy.id}`;
    const modalIntroId = `issue-resolve-archive-intro-${discrepancy.id}`;
    const notesFieldId = `issue-resolve-archive-notes-${discrepancy.id}`;
    const notesTrimmed = resolutionNotes.trim();
    const canSubmit = notesTrimmed.length > 0 && !isSubmitting;

    const handleResolveAndArchive = async () => {
        if (!notesTrimmed) {
            setValidationError('Add resolution notes before archiving this issue.');
            return;
        }

        setValidationError(null);
        setIsSubmitting(true);

        try {
            await resolveAndArchiveDiscrepancy(
                discrepancy,
                notesTrimmed,
                companyUser?.name || companyOwner?.name || 'Unknown',
            );
            setIssueCount((prev) => Math.max(0, prev - 1));
            setResolutionNotes('');
            await onArchived?.();
            onClose();
        } catch (error) {
            console.error(error);
            Sentry.captureException(error);
            setValidationError('Could not archive this issue. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="issues-modal-overlay" onClick={isSubmitting ? undefined : onClose}>
            <div
                ref={dialogRef}
                className="issues-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby={modalTitleId}
                aria-describedby={modalIntroId}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="issues-modal-header">
                    <h2 id={modalTitleId}>Archive {discrepancy.equipmentName}</h2>
                </div>
                <div className="issues-modal-body">
                    <p className="issues-modal-intro" id={modalIntroId}>
                        This issue will be marked <strong>{ITEM_STATUS_CONTENT.resolved}</strong> and moved to{' '}
                        <strong>Archived Issues</strong>. Resolution notes are required and will appear in issue
                        history.
                    </p>
                    <div className="issues-radio-group issues-radio-group--readonly">
                        <div className="issues-radio-option">
                            <input
                                type="radio"
                                id={`issue-resolve-archive-status-${discrepancy.id}`}
                                name={`issue-resolve-archive-status-${discrepancy.id}`}
                                checked
                                readOnly
                            />
                            <label htmlFor={`issue-resolve-archive-status-${discrepancy.id}`}>
                                Mark as {ITEM_STATUS_CONTENT.resolved}
                            </label>
                        </div>
                    </div>
                    <label className="issues-field-label" htmlFor={notesFieldId}>
                        Resolution notes <span className="issues-required-indicator">(required)</span>
                    </label>
                    <textarea
                        ref={notesRef}
                        id={notesFieldId}
                        className="issues-textarea"
                        placeholder="Describe how this issue was resolved"
                        value={resolutionNotes}
                        onChange={(e) => {
                            setResolutionNotes(e.target.value);
                            if (validationError && e.target.value.trim()) {
                                setValidationError(null);
                            }
                        }}
                        aria-invalid={validationError ? true : undefined}
                        aria-describedby={
                            validationError
                                ? `issue-resolve-archive-error-${discrepancy.id} ${modalIntroId}`
                                : modalIntroId
                        }
                    />
                    {validationError && (
                        <p className="issues-field-error" id={`issue-resolve-archive-error-${discrepancy.id}`} role="alert">
                            {validationError}
                        </p>
                    )}
                </div>
                <div className="issues-modal-footer">
                    <Btn
                        variant="secondary-outlined"
                        buttonSize="sm"
                        buttonShape="semi-square"
                        buttonText="Cancel"
                        buttonDisabled={isSubmitting}
                        buttonOnClick={onClose}
                    />
                    <Btn
                        variant="warning-outlined"
                        buttonSize="sm"
                        buttonShape="semi-square"
                        buttonText={isSubmitting ? 'Archiving...' : 'Resolve and archive'}
                        buttonDisabled={!canSubmit}
                        buttonAriaProps={isSubmitting ? { 'aria-busy': true } : undefined}
                        buttonOnClick={handleResolveAndArchive}
                    />
                </div>
            </div>
        </div>
    );
};
